/**
 * Driver-app FAQ content — rendered by src/pages/support/FaqPage.jsx
 * at /support/faq ("Help centre" on the Support page).
 *
 * Edit answers HERE, not in the page. Shape:
 *   topics: [{ id, title, items: [{ id, question, answer: [paragraph, …] }] }]
 * Ids are stable slugs (they become DOM ids); answers are plain-text
 * paragraphs (no HTML).
 *
 * Rule for this file: every answer describes what the app actually does
 * today (each one cites where that behaviour lives). No prices, no
 * timings and no policies the product does not enforce. If you are not
 * sure an answer is true, mark it with a `TODO(faq):` comment instead of
 * guessing.
 */

const FAQ_TOPICS = [
  {
    id: 'charging',
    title: 'Charging',
    items: [
      {
        // ChargerEntry (/c/:identifier) is the QR landing route;
        // FindChargerPage (/charge) lists chargers; ScanChargerButton is
        // native-only.
        id: 'start-charging',
        question: 'How do I start charging?',
        answer: [
          'Scan the QR code on the charger, or open Find Charger in the app and choose a charger. In the iOS and Android apps you can also use the Scan button.',
          'Follow the steps on screen. You confirm your charging details before charging starts.',
        ],
      },
      {
        // ChargeMonitoring "Stop Charging" action; chargerUx/copy.js
        // stopRequested; ENDED_EARLY "The connector was unplugged".
        id: 'stop-charging',
        question: 'How do I stop charging?',
        answer: [
          'Tap Stop Charging on the charging screen. The app shows "Stop requested" until the charger confirms that charging has stopped.',
          'Unplugging the connector also ends the session.',
        ],
      },
      {
        // ChargeMonitoring per-minute budget auto-stop.
        // TODO(faq): confirm whether a per-kWh session also stops by
        // itself when the selected hold amount is used up. Only the
        // per-minute auto-stop is visible in this app, so the answer
        // does not claim it for per-kWh.
        id: 'auto-stop',
        question: 'Will charging stop on its own?',
        answer: [
          'For chargers billed per minute, charging is stopped automatically when your session budget is reached.',
          'Charging also ends when you tap Stop Charging or unplug the connector.',
        ],
      },
      {
        // ActiveSessionPage at /charge/active; dashboard ActiveSessionCard.
        id: 'find-active-session',
        question: 'I closed the app. How do I get back to my session?',
        answer: [
          'Open the app and go to Home. A session that is still in progress is shown there, and tapping it takes you back to the charging screen.',
        ],
      },
      {
        // chargerUx/copy.js: FAILED_TO_START "No energy was delivered",
        // billing.notCharged.
        id: 'did-not-start',
        question: 'What if charging does not start?',
        answer: [
          'The charging screen tells you when a charge did not start and what you can do next, such as trying again or using another charger.',
          'If something still looks wrong, contact support and include the support reference shown on the screen, if there is one.',
        ],
      },
    ],
  },
  {
    id: 'payments',
    title: 'Payments and receipts',
    items: [
      {
        // AmountPicker "Pre-Authorization Hold"; PayPage hold copy.
        id: 'payment-hold',
        question: 'Why is there a hold on my card?',
        answer: [
          'Before a card-paid session starts you choose a pre-authorization amount. This is a temporary hold on your card, not a charge.',
          'When the session ends, only the actual cost of the session is taken and the rest of the hold is released.',
        ],
        // TODO(faq): how long a released hold takes to disappear from a
        // statement depends on the card issuer; no timing is stated here
        // on purpose. Add one only if support has an approved wording.
      },
      {
        // AmountPicker effectiveMinCents notice.
        id: 'minimum-charge',
        question: 'Is there a minimum amount?',
        answer: [
          'Some chargers have a minimum charge amount. When they do, it is shown on the screen where you choose your pre-authorization amount.',
        ],
      },
      {
        // PayPage "Confirm your card" (hold) vs "Pay for your charging
        // session" (final) modes.
        id: 'confirm-card-prompt',
        question: 'I was asked to confirm my card during or after charging. Why?',
        answer: [
          'If a card payment for a session could not be completed, the app asks you to confirm your card. During charging this is a temporary hold, and you do not need to stop or restart charging.',
          'For a session that has already finished, the page shows the exact amount you will be charged for that session.',
        ],
      },
      {
        // HistoryPage → SessionDetailPage → ReceiptEmailCard (when
        // offersCustomerReceipt) + native-only "Share receipt".
        id: 'receipts',
        question: 'How do I get a receipt?',
        answer: [
          'Open Activity and choose the session. When a receipt is available for it, use "Email me a receipt" to send it to an email address. In the iOS and Android apps you can also share the receipt from the same screen.',
        ],
      },
      {
        // AccountChoice benefits list.
        id: 'registered-vehicle-receipts',
        question: 'Who receives the receipt when I charge a registered vehicle?',
        answer: [
          'Receipts for a registered vehicle go to the vehicle’s registered owner.',
        ],
      },
    ],
  },
  {
    id: 'access',
    title: 'Complimentary and fleet charging',
    items: [
      {
        // utils/billingMode.js: FREE "Complimentary", FLEET_INVOICE
        // "Account billed", CREDIT "Reimbursement covered".
        id: 'billing-labels',
        question: 'What do "Complimentary" and "Account billed" mean?',
        answer: [
          'They describe how a session is paid for. "Complimentary" means the session is free of charge to you. "Account billed" means it is billed to the fleet or business account your vehicle belongs to. In both cases no card payment is needed.',
          '"Reimbursement covered" means the session was paid for with a redeem code credit.',
        ],
      },
      {
        // TODO(faq): who grants complimentary / fleet access, and how a
        // driver asks for it, is decided by the operator in the Admin
        // app; this answer only says what the driver sees. Confirm the
        // approved wording with support before expanding it.
        id: 'get-fleet-access',
        question: 'How do I get complimentary or fleet charging?',
        answer: [
          'Complimentary and fleet charging are set up for registered vehicles by GreenCharge or by the fleet or business your vehicle belongs to. When your vehicle has access at a charger, the app applies it automatically after you verify your vehicle.',
          'If you think your vehicle should have access and it does not, contact support.',
        ],
      },
      {
        // FleetAccessResult CARD_CHOICE_* copy.
        id: 'fleet-not-included',
        question: 'The app says this charger is not included in my fleet plan. Can I still charge?',
        answer: [
          'Yes. You can charge at the public rate by paying with a card, or choose another vehicle. If you pay by card, your fleet account is not charged.',
        ],
      },
      {
        // RedeemCodesPage empty-state copy + statuses; AmountPicker
        // redeem panel, single-use / no carry-forward hint.
        id: 'redeem-codes',
        question: 'What are redeem codes?',
        answer: [
          'Redeem codes are issued by GreenCharge support as a credit against a future charging session. Your codes are listed under Redeem Codes, marked Available, Redeemed, Expired or Cancelled.',
        ],
      },
      {
        id: 'use-redeem-code',
        question: 'How do I use a redeem code?',
        answer: [
          'Apply it on the screen where you choose your pre-authorization amount. If the credit covers the amount you selected, no card authorization is needed; otherwise only the difference is authorized on your card.',
          'A code can be used once. Any credit left unused in that session does not carry forward.',
        ],
      },
    ],
  },
  {
    id: 'vehicles',
    title: 'Vehicles and licence plates',
    items: [
      {
        // AccountChoice; ChargeSetup licence-plate field.
        id: 'registered-vehicle',
        question: 'What is a registered vehicle?',
        answer: [
          'A registered vehicle is recognised by its licence plate and a 4-digit vehicle PIN. When you charge it, the vehicle’s registered rate applies automatically. You can always continue as a guest and pay by card instead.',
        ],
      },
      {
        // VehiclesPage empty state.
        // TODO(faq): there is no "add vehicle" screen in the driver app;
        // vehicles arrive through registration/onboarding. Confirm how a
        // driver should register a new vehicle before saying more.
        id: 'vehicle-missing',
        question: 'My vehicle is not listed under My Vehicles. What should I do?',
        answer: [
          'My Vehicles shows the vehicles registered to your GreenCharge account. If you have registered a vehicle and do not see it, contact support so we can check the details on file.',
        ],
      },
      {
        // VehicleDetailPage plate-change request flow.
        id: 'change-plate',
        question: 'How do I change my licence plate?',
        answer: [
          'Open My Vehicles, choose the vehicle and tap "Request a licence plate change". The change is reviewed before it takes effect, and you can cancel a request while it is awaiting approval.',
          'If a request is not approved, the vehicle page says so and may include a note from the reviewer.',
        ],
      },
      {
        // ChargeSetup "License plate not recognized".
        id: 'plate-not-recognised',
        question: 'The app says my licence plate is not recognised. Why?',
        answer: [
          'The plate you entered does not match a registered vehicle. Check it is entered exactly as registered. If it still is not recognised, contact support.',
        ],
      },
      {
        // VehicleVerification lockout copy.
        id: 'pin-locked',
        question: 'I entered the wrong vehicle PIN too many times. What now?',
        answer: [
          'After too many incorrect attempts, PIN entry is locked for 10 minutes. You can still charge straight away by paying with a card.',
        ],
      },
      {
        // ChangePinFlow (/vehicles/change-pin/:token) copy.
        id: 'change-pin',
        question: 'How do I change my vehicle PIN?',
        answer: [
          'Vehicle PINs are changed with a Change-PIN link sent by email. If your link has expired or has already been used, ask your administrator for a new one.',
        ],
        // TODO(faq): confirm who a non-fleet driver should ask for a new
        // Change-PIN link ("your administrator" is the app's own wording).
      },
    ],
  },
  {
    id: 'account',
    title: 'Your account',
    items: [
      {
        // pages/auth/Auth.jsx OTP flow.
        id: 'sign-in',
        question: 'How do I sign in?',
        answer: [
          'Enter your email address and we send you a 6-digit code. Enter the code to sign in. If a code expires, request a new one.',
        ],
      },
      {
        // ProfilePage personal information + change-email dialog.
        id: 'update-details',
        question: 'How do I update my name, mobile number or email?',
        answer: [
          'Go to Profile. You can edit your name and mobile number there. To change your email address, tap Change next to it and confirm the new address with the verification code we send to it.',
        ],
      },
      {
        // ProfilePage sign-out confirm.
        id: 'sign-out',
        question: 'How do I sign out?',
        answer: [
          'Go to Profile and tap Sign out.',
        ],
      },
      {
        // No account-deletion flow exists in the driver app.
        // TODO(faq): confirm the approved process for closing an account
        // (and any data-retention wording) before adding more here.
        id: 'close-account',
        question: 'How do I close my account?',
        answer: [
          'Contact support from the Support page and we will help you.',
        ],
      },
    ],
  },
];

export default FAQ_TOPICS;
